import React, { useCallback, useRef, useState } from 'react';
import { Alert, Button, Modal } from 'react-bootstrap';
import { Quote } from 'lucide-react';
import { notify } from '../../../utils/notification';
import useCampaignSpellSync from '../hooks/useCampaignSpellSync';
import { decideCuttingWords, syncCuttingWords } from '../utils/cuttingWordsService';
import { applyBardicInspirationUpdate } from '../../../shared/rules/bard.mjs';
import './RollRequests.scss';

/** One line for what happened: the die, then the outcome. */
export const describeCuttingWords = done => {
  const die = `−${done.penalty} (d${done.sides})`;
  if (done.kind === 'damage') return `Cutting Words: ${die} · ${done.attackerName} deals ${done.damageAfter} before anything else reduces it.`;
  return `Cutting Words: ${die} · ${done.attackerName}'s attack ${done.attackTotalAfter >= done.armorClass ? 'still hits' : 'misses'} (${done.attackTotalAfter} vs AC ${done.armorClass}).`;
};

/**
 * Cutting Words (College of Lore 3) on a monster's attack: once per hit, subtract now, wait for the damage roll, or not now.
 * The DM waits, and can skip. The server rolls the die and spends the use and the Reaction.
 */
export default function CuttingWordsPrompt({ campaignId, characterId, sides, onCharacterChange, onCombatChange }) {
  const [windows, setWindows] = useState([]), [busy, setBusy] = useState(false), [error, setError] = useState('');
  const lock = useRef(false);
  const load = useCallback(async () => (await syncCuttingWords(campaignId, { characterId: String(characterId) })).windows || [], [campaignId, characterId]);
  useCampaignSpellSync({ campaignId: characterId ? campaignId : null, kind: 'fateWindows', load,
    onData: next => setWindows(previous => (JSON.stringify(previous) === JSON.stringify(next) ? previous : next)) });
  const offer = windows[0];
  const decide = async decision => {
    if (!offer || lock.current) return;
    lock.current = true; setBusy(true); setError('');
    try {
      const result = await decideCuttingWords(campaignId, offer.windowId, { helperId: String(characterId), decision });
      if (result.inspiration) onCharacterChange?.(previous => applyBardicInspirationUpdate(previous, result.inspiration));
      if (result.combat) onCombatChange?.(result.combat);
      if (decision === 'use' && result.window?.status === 'used') {
        const line = describeCuttingWords(result.window);
        notify(line, 'success');
        window.dispatchEvent(new CustomEvent('damage-roll', { detail: { value: result.window.penalty, source: `Cutting Words · ${offer.attackerName}`, breakdown: line } }));
      }
      setWindows(previous => previous.filter(item => item.windowId !== offer.windowId));
    } catch (failure) { setError(failure.message); }
    finally { lock.current = false; setBusy(false); }
  };
  if (!offer) return null;
  const damage = offer.kind === 'damage';
  return <Modal show centered backdrop="static" keyboard={false} className="dnd-modal modern-modal roll-request-prompt" aria-label="Cutting Words">
    <Modal.Body>
      <div className="roll-request__row">
        <span className="roll-request__muted"><Quote size={14} aria-hidden="true"/> Cutting Words · Reaction</span>
        {!damage && <span className="roll-request__pill">AC {offer.armorClass}</span>}
      </div>
      <div className="roll-request__title">{offer.attackerName} {damage ? 'rolled damage against' : 'hits'} {offer.targetName}</div>
      <div className="roll-request__muted">{damage ? `Damage roll ${offer.damage}. Subtract your d${sides} from it?` : `Attack roll ${offer.attackTotal} vs AC ${offer.armorClass}. Subtract your d${sides} now, or wait to see the damage?`}</div>
      {error && <Alert variant="danger" className="mt-2 mb-0 py-1 small">{error}</Alert>}
      <Button size="sm" variant="warning" className="w-100 mt-2" disabled={busy} onClick={() => decide('use')}>{busy ? 'Rolling…' : `Subtract d${sides} from the ${damage ? 'damage' : 'attack roll'}`}</Button>
      {!damage && <Button size="sm" variant="outline-light" className="w-100 mt-1" disabled={busy} onClick={() => decide('wait')}>Wait for the damage roll</Button>}
      <Button size="sm" variant="outline-light" className="w-100 mt-1" disabled={busy} onClick={() => decide('skip')}>Not now</Button>
    </Modal.Body>
  </Modal>;
}
